import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Apple, Play, Download, Smartphone } from 'lucide-react';
import { CardCorner, TranslatedText } from '../ui';
import { STORE_LINKS } from '../../constants/storeLinks';

export function DownloadCTA() {
  const { t } = useTranslation();

  return (
    <section className="relative py-24 md:py-32 overflow-hidden bg-transparent">
      {/* Semi-transparent overlay for readability */}
      <div className="absolute inset-0 bg-brand-darkNavy/60 backdrop-blur-sm" />
      {/* Animated background */}
      <div className="absolute inset-0">
        {/* Radial gradient */}
        <div className="absolute inset-0 bg-gradient-radial from-brand-yellow/10 via-transparent to-transparent" />
        
        {/* Animated lines */}
        <svg className="absolute inset-0 w-full h-full opacity-10">
          <defs>
            <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="transparent" />
              <stop offset="50%" stopColor="#FFD700" />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>
          </defs>
          {[...Array(5)].map((_, i) => (
            <motion.line
              key={i}
              x1="-100%"
              y1={`${20 + i * 15}%`}
              x2="200%"
              y2={`${20 + i * 15}%`}
              stroke="url(#lineGrad)"
              strokeWidth="1"
              initial={{ x1: '-100%', x2: '0%' }}
              animate={{ x1: '0%', x2: '100%' }}
              transition={{
                duration: 3 + i * 0.5,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
          ))}
        </svg>
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Main Card */}
          <div className="relative bg-brand-navy/80 backdrop-blur-xl border border-brand-yellow/30 rounded-3xl p-8 md:p-12 overflow-hidden">
            {/* Corner decorations */}
            <CardCorner position="top-left" />
            <CardCorner position="top-right" />
            <CardCorner position="bottom-left" />
            <CardCorner position="bottom-right" />
            
            {/* Badge */}
            <motion.div 
              className="flex justify-center mb-8"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, type: 'spring' }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/30">
                <Smartphone className="w-4 h-4 text-brand-yellow" />
                <span className="text-sm font-bold text-brand-yellow uppercase tracking-wider">{t('download.badge')}</span>
              </div>
            </motion.div>
            
            {/* Title */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6 text-center">
              <TranslatedText 
                i18nKey="download.title" 
                type="slideWords"
                duration={0.5}
                stagger={0.08}
              />
            </h2>
            
            {/* Subtitle */}
            <p className="text-lg md:text-xl text-dark-300 mb-12 max-w-2xl mx-auto text-center">
              <TranslatedText 
                i18nKey="download.subtitle" 
                type="fadeWords"
                duration={0.4}
                stagger={0.03}
              />
            </p>

            {/* App Store Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              <motion.a
                href={STORE_LINKS.appStore}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center gap-3 px-6 py-4 bg-brand-blue border border-brand-lightBlue/50 rounded-xl hover:border-brand-yellow/50 transition-colors group"
              >
                <Apple className="w-8 h-8 text-white" />
                <div className="text-left">
                  <div className="text-xs text-dark-400">{t('download.appStoreLabel')}</div>
                  <div className="text-lg font-bold text-white group-hover:text-brand-yellow transition-colors">{t('download.appStore')}</div>
                </div>
              </motion.a>
              
              <motion.a
                href={STORE_LINKS.googlePlay}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center gap-3 px-6 py-4 bg-brand-blue border border-brand-lightBlue/50 rounded-xl hover:border-brand-yellow/50 transition-colors group"
              >
                <Play className="w-8 h-8 text-white" />
                <div className="text-left">
                  <div className="text-xs text-dark-400">{t('download.googlePlayLabel')}</div>
                  <div className="text-lg font-bold text-white group-hover:text-brand-yellow transition-colors">{t('download.googlePlay')}</div>
                </div>
              </motion.a>

              <motion.a
                href={STORE_LINKS.appGallery}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center gap-3 px-6 py-4 bg-brand-blue border border-brand-lightBlue/50 rounded-xl hover:border-brand-yellow/50 transition-colors group"
              >
                <Smartphone className="w-8 h-8 text-white" />
                <div className="text-left">
                  <div className="text-xs text-dark-400">Disponible en</div>
                  <div className="text-lg font-bold text-white group-hover:text-brand-yellow transition-colors">AppGallery</div>
                </div>
              </motion.a>
            </div>
            
            {/* Animated download icon */}
            <motion.div
              className="absolute -right-10 -bottom-10 w-40 h-40 opacity-10"
              animate={{
                y: [0, -10, 0],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <Download className="w-full h-full text-brand-yellow" />
            </motion.div>
            
            {/* Shine effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 pointer-events-none"
              animate={{ x: ['-200%', '200%'] }}
              transition={{ duration: 4, repeat: Infinity, repeatDelay: 3 }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
